import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { xtreamApi } from '../services/xtreamApi';
import VideoPlayer from '../components/Player/VideoPlayer';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tv, Radio, ChevronRight, Clock, Calendar, Heart } from 'lucide-react';

const LiveTVPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [epgData, setEpgData] = useState(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [isLoadingEpg, setIsLoadingEpg] = useState(false);
  const [favSet, setFavSet] = useState(new Set());

  // Fetch categories and live favorites on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const data = await xtreamApi.getLiveCategories();
        setCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setIsLoadingCategories(false);
      }
    };
    fetchCategories();
    // Batch load all live favorites
    xtreamApi.getFavorites('live').then(favs => {
      setFavSet(new Set((favs || []).map(f => String(f.content_id))));
    }).catch(() => {});
  }, []);

  const toggleFav = async (e, channel) => {
    e.stopPropagation();
    const id = String(channel.stream_id);
    if (favSet.has(id)) {
      await xtreamApi.removeFavorite(id, 'live').catch(() => {});
      setFavSet(prev => { const n = new Set(prev); n.delete(id); return n; });
    } else {
      await xtreamApi.addFavorite(id, 'live', channel.name, channel.stream_icon, '').catch(() => {});
      setFavSet(prev => new Set(prev).add(id));
    }
  };

  // Fetch channels when category changes
  useEffect(() => {
    if (!selectedCategory) {
      setChannels([]);
      return;
    }

    const fetchChannels = async () => {
      try {
        setIsLoadingChannels(true);
        const data = await xtreamApi.getLiveStreams(selectedCategory.category_id);
        setChannels(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching channels:', err);
        setChannels([]);
      } finally {
        setIsLoadingChannels(false);
      }
    };
    fetchChannels();
  }, [selectedCategory]);

  // Fetch EPG when channel changes
  useEffect(() => {
    if (!selectedChannel) {
      setEpgData(null);
      return;
    }

    const fetchEpg = async () => {
      try {
        setIsLoadingEpg(true);
        const data = await xtreamApi.getShortEpg(selectedChannel.stream_id, 6);
        setEpgData(data);
      } catch (err) {
        console.error('Error fetching EPG:', err);
        setEpgData(null);
      } finally {
        setIsLoadingEpg(false);
      }
    };
    fetchEpg();
  }, [selectedChannel]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedChannel(null);
    setEpgData(null);
  };

  const handleChannelSelect = (channel) => {
    setSelectedChannel(channel);
  };

  const getStreamUrl = () => {
    if (!selectedChannel) return null;
    // Use .m3u8 (HLS) for live streams - handles buffering/segmentation much better than raw .ts
    return xtreamApi.getStreamUrl(selectedChannel.stream_id, 'live', 'm3u8');
  };

  const formatEpgTime = (timestamp) => {
    if (!timestamp) return '';
    // Handle both string and number timestamps
    const ts = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;
    if (isNaN(ts)) return '';
    const date = new Date(ts * 1000);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatEpgDate = (timestamp) => {
    if (!timestamp) return '';
    const ts = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;
    if (isNaN(ts)) return '';
    const date = new Date(ts * 1000);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Decode base64 EPG data
  const decodeBase64 = (str) => {
    if (!str) return '';
    try {
      return decodeURIComponent(escape(atob(str)));
    } catch (e) {
      try {
        return atob(str);
      } catch (e2) {
        return str; // Return original if not base64
      }
    }
  };

  if (isLoadingCategories) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center pt-16">
        <LoadingSpinner size="large" text="Loading Live TV..." />
      </div>
    );
  }

  return (
    <div data-testid="livetv-page" className="min-h-screen bg-[#0a0a0b] pt-16">
      <div className="flex h-[calc(100vh-64px)]">
        {/* Categories Column */}
        <div data-testid="livetv-categories-col" className="w-64 flex-shrink-0 bg-[#0d0d0f] border-r border-gray-800">
          <div className="p-4 border-b border-gray-800">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <Tv style={{color:"var(--accent)"}} className="w-5 h-5" />
              Categories
            </h2>
            <p className="text-gray-500 text-xs mt-1">{categories.length} categories</p>
          </div>
          <ScrollArea className="h-[calc(100%-80px)]">
            <div className="p-2">
              {categories.map((category) => (
                <button
                  key={category.category_id}
                  onClick={() => handleCategorySelect(category)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg mb-1 transition-all flex items-center justify-between group ${
                    selectedCategory?.category_id === category.category_id
                      ? 'text-white'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                  style={selectedCategory?.category_id === category.category_id ? { backgroundColor: 'var(--accent)' } : {}}
                >
                  <span className="text-sm truncate">{category.category_name}</span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${
                    selectedCategory?.category_id === category.category_id ? 'rotate-90' : ''
                  }`} />
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Channels Column */}
        <div className={`w-80 flex-shrink-0 bg-[#0f0f11] border-r border-gray-800 transition-all ${
          selectedCategory ? 'opacity-100' : 'opacity-50'
        }`}>
          <div className="p-4 border-b border-gray-800">
            <h2 className="text-white font-semibold">
              {selectedCategory ? selectedCategory.category_name : 'Select Category'}
            </h2>
            <p className="text-gray-500 text-xs mt-1">
              {channels.length} channels
            </p>
          </div>
          <div className="overflow-y-auto h-[calc(100%-80px)]">
            {isLoadingChannels ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner size="small" text="" />
              </div>
            ) : (
              <div className="p-2">
                {channels.map((channel) => (
                  <div
                    key={channel.stream_id}
                    onClick={() => handleChannelSelect(channel)}
                    className={`w-full text-left p-2 rounded-lg mb-1 transition-all flex items-center gap-2 cursor-pointer overflow-hidden`}
                    style={selectedChannel?.stream_id === channel.stream_id
                      ? { backgroundColor: 'var(--accent-bg)', boxShadow: 'inset 0 0 0 1px var(--accent)' }
                      : {}}
                  >
                    {/* Channel Logo */}
                    <div className="w-8 h-8 rounded bg-gray-800 flex-shrink-0 flex items-center justify-center overflow-hidden">
                      {channel.stream_icon ? (
                        <img
                          src={channel.stream_icon}
                          alt=""
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <Radio className="w-5 h-5 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0 flex items-center">
                      <div className="flex-1 min-w-0 mr-1">
                        <p className={`text-sm truncate ${
                          selectedChannel?.stream_id === channel.stream_id ? 'text-white' : 'text-gray-300'
                        }`}>
                          {channel.name}
                        </p>
                        {channel.tv_archive === 1 && (
                          <span className="text-xs" style={{color:"var(--accent-light)"}}>Catchup</span>
                        )}
                      </div>
                      <button
                        onClick={(e) => toggleFav(e, channel)}
                        className="flex-shrink-0"
                        title={favSet.has(String(channel.stream_id)) ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Heart className={`w-4 h-4 transition-colors ${favSet.has(String(channel.stream_id)) ? 'text-red-500 fill-red-500' : 'text-gray-400 hover:text-red-400'}`} />
                      </button>
                    </div>
                  </div>
                ))}
                {!selectedCategory && (
                  <p className="text-gray-500 text-center py-8 text-sm">
                    Select a category to view channels
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Video Preview & EPG */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Video Preview - fills width, fixed height */}
          <div className="h-[45%] min-h-[200px] bg-black relative flex-shrink-0">
            {selectedChannel ? (
              <VideoPlayer
                src={getStreamUrl()}
                poster={selectedChannel.stream_icon}
                title={selectedChannel.name}
                onBack={() => setSelectedChannel(null)}
                isLive={true}
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                <Tv className="w-20 h-20 mb-4 opacity-30" />
                <p>Select a channel to start watching</p>
              </div>
            )}
          </div>

          {/* EPG Data - takes remaining space */}
          <div className="flex-1 min-h-0 bg-[#0d0d0f] border-t border-gray-800 flex flex-col">
            <div className="p-4 border-b border-gray-800">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Calendar style={{color:"var(--accent)"}} className="w-4 h-4" />
                Program Guide
              </h3>
            </div>
            <ScrollArea className="flex-1">
              {isLoadingEpg ? (
                <div className="p-4 flex justify-center">
                  <LoadingSpinner size="small" text="" />
                </div>
              ) : epgData?.epg_listings && epgData.epg_listings.length > 0 ? (
                <div className="p-3 space-y-2">
                  {epgData.epg_listings.map((program, index) => {
                    // Parse timestamps - use start_timestamp/stop_timestamp first, fall back to start/stop
                    const startTs = program.start_timestamp 
                      ? parseInt(program.start_timestamp) 
                      : (typeof program.start === 'string' && program.start.includes('-') 
                          ? Math.floor(new Date(program.start).getTime() / 1000)
                          : parseInt(program.start));
                    const stopTs = program.stop_timestamp 
                      ? parseInt(program.stop_timestamp) 
                      : (program.end 
                          ? (typeof program.end === 'string' && program.end.includes('-')
                              ? Math.floor(new Date(program.end).getTime() / 1000)
                              : parseInt(program.end))
                          : (typeof program.stop === 'string' && program.stop.includes('-')
                              ? Math.floor(new Date(program.stop).getTime() / 1000)
                              : parseInt(program.stop)));
                    
                    const nowTs = Math.floor(Date.now() / 1000);
                    const isNow = startTs && stopTs && nowTs >= startTs && nowTs <= stopTs;
                    
                    // Decode base64 title and description
                    const title = decodeBase64(program.title) || 'Unknown Program';
                    const description = decodeBase64(program.description);
                    
                    return (
                      <div
                        key={index}
                        className={`p-3 rounded-lg ${isNow ? '' : 'bg-gray-800/50'}`}
                        style={isNow ? { backgroundColor: 'var(--accent-bg)', boxShadow: 'inset 0 0 0 1px var(--accent)' } : {}}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {isNow && (
                            <span className="px-2 py-0.5 bg-red-600 text-white text-xs rounded font-medium">
                              NOW
                            </span>
                          )}
                          <span className="text-gray-400 text-xs flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatEpgTime(startTs)} - {formatEpgTime(stopTs)}
                          </span>
                        </div>
                        <p className="text-white text-sm font-medium">
                          {title}
                        </p>
                        {description && (
                          <p className="text-gray-400 text-xs mt-1 line-clamp-2">
                            {description}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {selectedChannel ? 'No program guide available' : 'Select a channel to view program guide'}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveTVPage;
